import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getSyncQueue, processSyncQueue, clearSyncedItems } from '@/lib/offlineStorage';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SyncManager = () => {
  const { token } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Update pending count from sync queue
  const updatePendingCount = useCallback(async () => {
    try {
      const queue = await getSyncQueue();
      setPendingCount(queue.length);
    } catch (error) {
      console.error('Error getting sync queue:', error);
    }
  }, []);

  // Handle online/offline status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing data...');
      // Trigger sync when coming back online
      handleSync();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will be saved locally.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    updatePendingCount();

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_NAS_DATA' || event.data.type === 'SYNC_ARBE_DATA') {
          handleSync();
        }
      });
    }

    // Check pending count periodically
    const interval = setInterval(updatePendingCount, 10000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [updatePendingCount]);

  // Process sync queue
  const handleSync = async () => {
    if (!isOnline || isSyncing || !token) return;

    setIsSyncing(true);
    try {
      const result = await processSyncQueue(`${API}`, token);
      
      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} item(s) successfully`);
      }
      
      if (result.failed > 0) {
        toast.error(`Failed to sync ${result.failed} item(s)`);
        console.error('Sync errors:', result.errors);
      }
      
      setLastSyncTime(new Date());
      await updatePendingCount();
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync data');
    } finally {
      setIsSyncing(false);
    }
  };

  // Register for background sync
  const registerBackgroundSync = async () => {
    if ('serviceWorker' in navigator && 'sync' in window.registration) {
      try {
        await navigator.serviceWorker.ready;
        await window.registration.sync.register('sync-nas-data');
        console.log('Background sync registered');
      } catch (error) {
        console.error('Background sync registration failed:', error);
      }
    }
  };

  // Don't render if online and nothing pending
  if (isOnline && pendingCount === 0 && !showDetails) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Sync Status Badge */}
      <div 
        className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg cursor-pointer transition-all ${
          isOnline 
            ? pendingCount > 0 
              ? 'bg-amber-500 text-white hover:bg-amber-600' 
              : 'bg-green-500 text-white hover:bg-green-600'
            : 'bg-red-500 text-white hover:bg-red-600'
        }`}
        onClick={() => setShowDetails(!showDetails)}
      >
        {isOnline ? (
          pendingCount > 0 ? (
            <>
              <Cloud size={18} />
              <span className="text-sm font-medium">{pendingCount} pending</span>
            </>
          ) : (
            <>
              <Check size={18} />
              <span className="text-sm font-medium">Synced</span>
            </>
          )
        ) : (
          <>
            <WifiOff size={18} />
            <span className="text-sm font-medium">Offline</span>
          </>
        )}
      </div>

      {/* Expanded Details */}
      {showDetails && (
        <div className="absolute bottom-14 right-0 w-72 bg-white rounded-lg shadow-xl border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-slate-800">Sync Status</h4>
            <button onClick={() => setShowDetails(false)} className="text-slate-400 hover:text-slate-600">
              ×
            </button>
          </div>

          {/* Connection Status */}
          <div className={`flex items-center gap-2 p-2 rounded-lg ${isOnline ? 'bg-green-50' : 'bg-red-50'}`}>
            {isOnline ? (
              <>
                <Wifi size={18} className="text-green-600" />
                <span className="text-sm text-green-700">Connected</span>
              </>
            ) : (
              <>
                <WifiOff size={18} className="text-red-600" />
                <span className="text-sm text-red-700">No internet connection</span>
              </>
            )}
          </div>

          {/* Pending Items */}
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
              <CloudOff size={18} className="text-amber-600" />
              <span className="text-sm text-amber-700">{pendingCount} item(s) pending sync</span>
            </div>
          )}

          {/* Last Sync Time */}
          {lastSyncTime && (
            <p className="text-xs text-slate-500">
              Last synced: {lastSyncTime.toLocaleTimeString()}
            </p>
          )}

          {/* Sync Button */}
          <button
            onClick={handleSync}
            disabled={!isOnline || isSyncing || pendingCount === 0}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
              isOnline && pendingCount > 0
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
          >
            <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>

          {/* Info */}
          <p className="text-xs text-slate-500">
            {isOnline 
              ? 'Your data syncs automatically when online.' 
              : 'Changes will sync when you reconnect.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default SyncManager;
